import { CommonModule } from '@angular/common';
import { Component, computed, Inject, OnInit, signal } from '@angular/core';
import { Subscription } from 'rxjs';
import { MarkdownModule } from 'ngx-markdown';

import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatTabsModule } from '@angular/material/tabs';
import { MatIconModule } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';

import Resources from '@constants/resourses';

import { SupplierTariffComponent } from 'src/app/feature/quotes-result/components/supplier-tariff/supplier-tariff.component';

import { UtilityService } from '@services/utility.service';
import { SessionService } from '@services/session.service';
import { IQuotesTariff } from 'src/app/core/models/quotesTariff.interface';
import { GoogleAnalyticsService } from '@services/google-analytics.service';
import { TealiumUtagService } from '@services/utag.service';
@Component({
  selector: 'app-tariff-details-dialog',
  standalone: true,
  imports: [CommonModule, MatTabsModule, MatIconModule, MatExpansionModule, MarkdownModule, MatDialogModule, SupplierTariffComponent],
  templateUrl: './tariff-details-dialog.component.html',
  styleUrl: './tariff-details-dialog.component.scss'
})
export class TariffDetailsDialogComponent implements OnInit {
  subscription: Subscription[] = [];
  id: string = '';
  tealiumData: any = this.sessionService.getItemFromSession(Resources.SessionStorageKeys.TealiumData) || {};
  currentSupplier: any = this.sessionService.getItemFromLocal(Resources.SessionStorageKeys.QuotesData)?.CurrentSupplier;

  isTablet: boolean = window.innerWidth <= 1024 && window.innerWidth > 767;

  elecCurrentUsageAnnually: any = signal(0);
  elecCurrentUsageMonthly: any = computed(() => this.utilityService.formatNumber(this.elecCurrentUsageAnnually() / 12));

  elecSavingPerYear: any = signal(0);
  elecSavingPerMonth: any = computed(() => this.utilityService.formatNumber(this.elecSavingPerYear() / 12));

  gasCurrentUsageAnnually: any = signal(0);
  gasCurrentUsageMonthly: any = computed(() => this.utilityService.formatNumber(this.gasCurrentUsageAnnually() / 12));

  gasSavingPerYear: any = signal(0);
  gasSavingPerMonth: any = computed(() => this.utilityService.formatNumber(this.gasSavingPerYear() / 12));

  isNegativeNumber: any = (num) => this.utilityService.isNegativeNumber(num);

  content: any = {
    savingCalculated: '',
    claimRewards: {
      title: '',
      content: ''
    }
  };

  abVariant: string;
  ctmFlowVariant: string;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<TariffDetailsDialogComponent>,
    private utilityService: UtilityService,
    private sessionService: SessionService,
    private googleAnalytics: GoogleAnalyticsService,
    private tealiumService: TealiumUtagService
  ) {
    this.id = this.tealiumData['app_name'] + '_' + this.tealiumData['page_name'] + '_TariffInformation';

    this.elecCurrentUsageAnnually.set(Number(this.currentSupplier?.Fuels?.Electricity?.AnnualConsumption));
    this.gasCurrentUsageAnnually.set(Number(this.currentSupplier?.Fuels?.Gas?.AnnualConsumption));

    this.elecSavingPerYear.set(Number(this.data?.selectedQuote?.Fuels?.Electricity?.SavingsPerYear));
    this.gasSavingPerYear.set(Number(this.data?.selectedQuote?.Fuels?.Gas?.SavingsPerYear));

    this.subscription.push(
      this.utilityService.getMDFile('quotesPage/quotePage-tariffDetails-savingCalculated.md').subscribe((res) => {
        this.content.savingCalculated = res;
      })
    );

    this.subscription.push(
      this.utilityService.getMDFile('quotesPage/quotePage-tariffDetails-claimRewards.md').subscribe((res) => {
        const splitData: any[] = res.split('\n\n');
        this.content.claimRewards['title'] = splitData[0];
        this.content.claimRewards['content'] = splitData[1];
      })
    );
  }

  ngOnInit(): void {
    // Initialize abVariant
    this.abVariant = this.utilityService.getCookie(Resources.ABtestingKeys.LANDING_PAGE_VARIANT_COOKIE) || 'A';
    this.ctmFlowVariant = this.utilityService.getCookie(Resources.ABtestingKeys.CTM_FLOW_VARIANT_COOKIE) || 'A';
    // Set initial value if not already set
    if (this.data && !this.data.isSmallScreen) {
      this.data.isSmallScreen = window.innerWidth <= 767;
    }

    // Tealium tariff detail view event
    this.tealiumData['page_name'] = 'YourTariffs';
    this.tealiumData['page_type'] = 'TariffDetails';

    this.tealiumData['brand_code'] = this.data?.selectedQuote?.SupplierName || '';
    this.tealiumData['brand_name'] = this.data?.selectedQuote?.SupplierName || '';
    this.tealiumData['brand_price'] = this.data?.selectedQuote?.EstimatedYearlyCost;
    this.tealiumData['energy_product'] = this.data?.selectedQuote?.TariffName || '';
    this.tealiumData['energy_saving'] = (this.elecSavingPerYear() + this.gasSavingPerYear()) || 0;
    this.tealiumData['energy_fuel_type'] = this.data?.selectedQuote?.Fuels ? Object.keys(this.data?.selectedQuote?.Fuels).join('_') : '';
    this.tealiumData['energy_tariff_duration'] = this.data?.selectedQuote?.PricesFixedUntil !== 'N/A' ? this.utilityService.getTotalMonthFromToday(this.data?.selectedQuote?.PricesFixedUntil, 'month') + ' months' : this.data?.selectedQuote?.PricesFixedUntil;

    this.tealiumService.viewEvent(this.tealiumData);
  }

  // Method to handle the click event of the "Switch Now" button
  switchNow(selectedSwitchDetails: IQuotesTariff): void {
    this.googleAnalytics.track({
      from: 'tariff_details_dialog',
      supplier_name: selectedSwitchDetails?.SupplierName,
      tariff_name: selectedSwitchDetails?.TariffName,
      abVariant: this.abVariant,
      ctmFlowVariant: this.ctmFlowVariant
    }, 'switch_now');
    // Close the dialog and pass the selected switch details
    this.dialogRef.close(selectedSwitchDetails);
  }

  // Method to close the dialog
  closeDialog(): void {
    this.tealiumData['event_action'] = 'onClick';
    this.tealiumData['event_category'] = '';
    this.tealiumData['event_label'] = 'CloseTariffDetails';

    this.tealiumService.linkEvent(this.tealiumData);

    this.dialogRef.close();
  }
}
